import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useToast,
  Select,
  IconButton,
  Text,
  Badge,
  Center,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Container,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  TableContainer,
  Flex,
  Spacer,
  Tooltip
} from '@chakra-ui/react';
import { DeleteIcon, SearchIcon, ArrowBackIcon, LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { userService } from '../config/index';
import { User, UserRole } from '../types/user';
import { AdminRoute } from '../components/AdminRoute';
import { useNavigate, Link } from 'react-router-dom';

export const AdminUsers = () => {
  const [users, setUsers] = useState<(User & { id: string })[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<(User & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [isRoleChangeDialogOpen, setIsRoleChangeDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSuspendDialogOpen, setIsSuspendDialogOpen] = useState(false);
  const [suspendAction, setSuspendAction] = useState<{ userId: string; suspend: boolean } | null>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState<string | null>(null);
  const [passwordUserEmail, setPasswordUserEmail] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  const toast = useToast();
  const navigate = useNavigate();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const brandRed = useColorModeValue('red.500', 'red.300');
  const tableBg = useColorModeValue('white', 'gray.700');

  const loadUsers = async () => {
    setLoading(true);
    try {
      console.log('Iniciando carga de usuarios...');
      const allUsers = await userService.getAllUsers();
      console.log('Usuarios obtenidos:', allUsers.length);
      console.log('Datos de usuarios:', allUsers);
      
      if (allUsers.length === 0) {
        console.warn('No se encontraron usuarios en la base de datos');
      }
      
      setUsers(allUsers);
      setFilteredUsers(allUsers);
      
      toast({
        title: `Cargados ${allUsers.length} usuarios`,
        description: 'La lista de usuarios se ha actualizado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error detallado al cargar usuarios:', error);
      toast({
        title: 'Error al cargar usuarios',
        description: error instanceof Error ? error.message : 'Error desconocido',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(user => 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleRoleChangeConfirm = async () => {
    if (!selectedUserId || !selectedRole) return;
    
    try {
      await userService.updateUser(selectedUserId, { role: selectedRole });
      toast({
        title: 'Rol actualizado',
        status: 'success',
        duration: 3000,
      });
      loadUsers();
    } catch (error) {
      toast({
        title: 'Error al actualizar rol',
        description: error instanceof Error ? error.message : 'Error desconocido',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsRoleChangeDialogOpen(false);
      setSelectedUserId(null);
      setSelectedRole(null);
    }
  };

  const handleRoleChangeClick = (userId: string, newRole: UserRole) => {
    setSelectedUserId(userId);
    setSelectedRole(newRole);
    setIsRoleChangeDialogOpen(true);
  };

  const handleDeleteClick = (userId: string) => {
    setSelectedUserId(userId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUserId) return;
    
    try {
      await userService.deleteUser(selectedUserId);
      toast({
        title: 'Usuario eliminado',
        status: 'success',
        duration: 3000,
      });
      loadUsers();
    } catch (error) {
      toast({
        title: 'Error al eliminar usuario',
        description: error instanceof Error ? error.message : 'Error desconocido',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedUserId(null);
    }
  };

  const handleSuspendClick = (userId: string, currentStatus: boolean) => {
    setSuspendAction({ userId, suspend: !currentStatus });
    setIsSuspendDialogOpen(true);
  };

  const handleSuspendConfirm = async () => {
    if (!suspendAction) return;
    
    try {
      await userService.toggleUserSuspension(suspendAction.userId, suspendAction.suspend);
      toast({
        title: suspendAction.suspend ? 'Usuario suspendido' : 'Usuario reactivado',
        status: 'success',
        duration: 3000,
      });
      loadUsers();
    } catch (error) {
      toast({
        title: 'Error al cambiar estado de suspensión',
        description: error instanceof Error ? error.message : 'Error desconocido',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSuspendDialogOpen(false);
      setSuspendAction(null);
    }
  };

  const handlePasswordChangeClick = (userId: string, userEmail: string) => {
    setPasswordUserId(userId);
    setPasswordUserEmail(userEmail);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setIsPasswordModalOpen(true);
  };

  const handlePasswordChangeConfirm = async () => {
    if (!passwordUserId) return;
    if (newPassword.length < 6) {
      toast({
        title: 'Contraseña muy corta',
        description: 'La contraseña debe tener al menos 6 caracteres.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Las contraseñas no coinciden',
        description: 'La nueva contraseña y su confirmación deben ser iguales.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    setIsChangingPassword(true);
    try {
      await userService.changeUserPassword(passwordUserId, newPassword);
      toast({
        title: 'Contraseña actualizada',
        description: `La contraseña de ${passwordUserEmail} ha sido cambiada correctamente.`,
        status: 'success',
        duration: 4000,
      });
      setIsPasswordModalOpen(false);
    } catch (error) {
      toast({
        title: 'Error al cambiar contraseña',
        description: error instanceof Error ? error.message : 'Error desconocido',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsChangingPassword(false);
      setPasswordUserId(null);
      setPasswordUserEmail('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  if (loading) {
    return (
      <Box minH="100vh">
        <Center h="100vh">
          <Spinner size="xl" color={brandRed} />
        </Center>
      </Box>
    );
  }

  return (
    <AdminRoute>
      <Box 
        minH="100vh" 
        backgroundImage="url('/silla.jpg')"
        backgroundSize="cover"
        backgroundPosition="center"
        position="relative"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="whiteAlpha.700"
        />
        <Container maxW="container.xl" position="relative" zIndex={1} py={8}>
        <Flex direction="column" gap={6}>
          <Box 
            position="relative"
            bg="whiteAlpha.500"
            p={{ base: 2, md: 3 }}
            borderRadius="xl"
            backdropFilter="blur(10px)"
            display="flex"
            alignItems="center"
            minH={{ base: "60px", md: "70px" }}
            mb={6}
          >
            <Link to="/">
              <Box 
                as="img" 
                src="/famma.png" 
                alt="Logo Famma" 
                className="logo" 
                h={{ base: "45px", md: "55px" }}
                objectFit="contain"
                transition="transform 0.2s ease"
                _hover={{ transform: 'scale(1.05)' }}
                ml={{ base: 2, md: 3 }}
              />
            </Link>
            <Box flex="1" textAlign="center">
              <Text
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
              >
                Gestión de Usuarios
              </Text>
            </Box>
            <Box w={{ base: "45px", md: "55px" }} /> {/* Spacer para balancear el logo */}
          </Box>

          <Box 
            bg="white" 
            p={6} 
            borderRadius="lg" 
            boxShadow="sm"
            mb={6}
          >
            <Flex direction="column" gap={4}>
              <Flex align="center" gap={4}>
                <IconButton
                  aria-label="Volver al dashboard"
                  icon={<ArrowBackIcon />}
                  onClick={() => navigate('/dashboard')}
                  colorScheme="red"
                  variant="ghost"
                />
                <Text fontSize="2xl" fontWeight="bold" color={brandRed}>Gestión de Usuarios</Text>
                <Spacer />
                <Button 
                  colorScheme="red"
                  variant="solid"
                  onClick={() => navigate('/admin/users/new')}
                >
                  Crear Usuario
                </Button>
              </Flex>

              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar por email o rol..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Flex>
          </Box>

          <Box mb={4}>
            <Button 
              leftIcon={<ArrowBackIcon transform="rotate(45deg)" />}
              onClick={loadUsers}
              colorScheme="blue"
              size="sm"
              ml="auto"
              display="block"
            >
              Actualizar lista de usuarios
            </Button>
          </Box>

          {filteredUsers.length === 0 ? (
            <Box 
              bg="white" 
              p={6} 
              borderRadius="lg" 
              boxShadow="sm"
              textAlign="center"
            >
              <Text fontSize="lg" color="gray.500">
                {searchTerm 
                  ? 'No se encontraron usuarios que coincidan con tu búsqueda.' 
                  : 'No se encontraron usuarios en la base de datos.'}
              </Text>
              <Text mt={2} fontSize="sm" color="gray.400">
                {users.length 
                  ? 'Intenta con otra búsqueda o actualiza la lista.'
                  : 'Hay 7 usuarios en la base de datos según la verificación, pero no se pudieron cargar. Actualiza la página o contacta al administrador.'}
              </Text>
            </Box>
          ) : (
            <TableContainer>
              <Table variant="simple" bg={tableBg} boxShadow="md" borderRadius="lg">
                <Thead>
                  <Tr>
                    <Th>Email</Th>
                    <Th>Rol</Th>
                    <Th>Estado</Th>
                    <Th>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredUsers.map(user => (
                  <Tr key={user.id}>
                    <Td>{user.email}</Td>
                    <Td>
                      <Select
                        value={user.role}
                        onChange={(e) => handleRoleChangeClick(user.id, e.target.value as UserRole)}
                        width="auto"
                      >
                        <option value="Admin">Admin</option>
                        <option value="Arquitecto">Arquitecto</option>
                        <option value="Jurídico">Jurídico</option>
                        <option value="Administrativo">Administrativo</option>
                      </Select>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={user.suspended ? 'red' : 'green'}
                        cursor="pointer"
                        onClick={() => handleSuspendClick(user.id, user.suspended || false)}
                        px={2}
                        py={1}
                        borderRadius="full"
                      >
                        {user.suspended ? 'Suspendido' : 'Activo'}
                      </Badge>
                    </Td>
                    <Td>
                      <Flex gap={2}>
                        <Tooltip label="Cambiar contraseña" hasArrow>
                          <IconButton
                            aria-label="Cambiar contraseña"
                            icon={<LockIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handlePasswordChangeClick(user.id, user.email)}
                          />
                        </Tooltip>
                        <Tooltip label="Eliminar usuario" hasArrow>
                          <IconButton
                            aria-label="Eliminar usuario"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            onClick={() => handleDeleteClick(user.id)}
                          />
                        </Tooltip>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            </TableContainer>
          )}
        </Flex>

        <AlertDialog
          isOpen={isRoleChangeDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setIsRoleChangeDialogOpen(false)}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>Confirmar cambio de rol</AlertDialogHeader>
              <AlertDialogBody>
                ¿Estás seguro de que quieres cambiar el rol de este usuario?
                Este cambio afectará a los permisos y accesos del usuario.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button onClick={() => setIsRoleChangeDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button colorScheme="red" ml={3} onClick={handleRoleChangeConfirm}>
                  Confirmar
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <AlertDialog
          isOpen={isDeleteDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setIsDeleteDialogOpen(false)}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>Eliminar usuario</AlertDialogHeader>
              <AlertDialogBody>
                ¿Estás seguro? Esta acción no se puede deshacer.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setIsDeleteDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button colorScheme="red" ml={3} onClick={handleDeleteConfirm}>
                  Eliminar
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <AlertDialog
          isOpen={isSuspendDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setIsSuspendDialogOpen(false)}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>
                {suspendAction?.suspend ? 'Suspender usuario' : 'Reactivar usuario'}
              </AlertDialogHeader>
              <AlertDialogBody>
                {suspendAction?.suspend
                  ? '¿Estás seguro de que quieres suspender a este usuario? El usuario no podrá acceder al sistema mientras esté suspendido.'
                  : '¿Estás seguro de que quieres reactivar a este usuario? El usuario volverá a tener acceso al sistema.'}
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setIsSuspendDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button colorScheme="red" ml={3} onClick={handleSuspendConfirm}>
                  Confirmar
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
        {/* Modal: Cambiar contraseña */}
        <Modal
          isOpen={isPasswordModalOpen}
          onClose={() => setIsPasswordModalOpen(false)}
          isCentered
        >
          <ModalOverlay backdropFilter="blur(4px)" />
          <ModalContent>
            <ModalHeader>Cambiar contraseña</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={4}>
              <Text mb={4} fontSize="sm" color="gray.600">
                Estableciendo nueva contraseña para: <strong>{passwordUserEmail}</strong>
              </Text>
              <FormControl mb={4}>
                <FormLabel>Nueva contraseña</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 6 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? 'Ocultar' : 'Mostrar'}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormHelperText>Al menos 6 caracteres.</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Confirmar contraseña</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Repite la contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </InputGroup>
                {confirmPassword && newPassword !== confirmPassword && (
                  <FormHelperText color="red.500">Las contraseñas no coinciden.</FormHelperText>
                )}
                {confirmPassword && newPassword === confirmPassword && newPassword.length >= 6 && (
                  <FormHelperText color="green.500">Las contraseñas coinciden ✓</FormHelperText>
                )}
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={() => setIsPasswordModalOpen(false)}
                isDisabled={isChangingPassword}
              >
                Cancelar
              </Button>
              <Button
                colorScheme="blue"
                onClick={handlePasswordChangeConfirm}
                isLoading={isChangingPassword}
                loadingText="Cambiando..."
                isDisabled={
                  newPassword.length < 6 ||
                  newPassword !== confirmPassword ||
                  isChangingPassword
                }
              >
                Cambiar contraseña
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        </Container>
      </Box>
    </AdminRoute>
  );
};
